// T113-T117: Dashboard with server-side evaluation and caching (Feature 022-client-ux-internal-ids)
// T011: Added six live KPI tiles (Feature 034-dashboard-search-filter-owner-fixes)
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getDashboardMetrics, getDashboardSummary, getTaskSummary } from '@/services/api';
import type { DashboardMetrics, DashboardSummary } from '@/types';

// T011: Tooltip definitions for each KPI tile (FR-009)
// T009/T017 (043): Corrected openLeads/closedLeadsToday tooltips; added three prospect tiles.
const KPI_TILES: Array<{
  key: keyof DashboardSummary;
  label: string;
  tooltip: string;
}> = [
  { key: 'newTickets',           label: 'New Tickets',             tooltip: 'Open tickets awaiting action' },
  { key: 'newLeads',             label: 'New Leads',               tooltip: 'New leads not yet contacted' },
  { key: 'openTickets',          label: 'Open Tickets',            tooltip: 'In Progress and Waiting on Client tickets' },
  { key: 'openLeads',            label: 'Open Leads',              tooltip: 'Leads contacted and awaiting progression' },
  { key: 'closedTicketsToday',   label: 'Closed Tickets Today',    tooltip: 'Resolved and Closed tickets today (UTC)' },
  { key: 'closedLeadsToday',     label: 'Closed Leads Today',      tooltip: 'Leads that exited the pipeline today (Qualified or Lost)' },
  { key: 'newProspectsToday',    label: 'New Prospects Today',     tooltip: 'Prospects promoted from a lead today' },
  { key: 'openProspects',        label: 'Open Prospects',          tooltip: 'Prospects still in the active pipeline' },
  { key: 'closedProspectsToday', label: 'Closed Prospects Today',  tooltip: 'Prospects that closed today (Qualified or Lost)' },
];

// T011: Individual KPI tile with accessible tooltip and error state (FR-009, FR-009a)
function KpiTile({
  label,
  tooltip,
  value,
  isError,
  onRetry,
}: {
  label: string;
  tooltip: string;
  value?: number;
  isError?: boolean;
  onRetry?: () => void;
}) {
  return (
    <div
      className="bg-white rounded shadow p-4 flex flex-col"
      title={tooltip}
      aria-label={`${label}: ${isError ? 'error' : (value ?? 0)}`}
    >
      {isError ? (
        <>
          <span className="text-3xl font-bold text-brand-text-secondary" aria-hidden="true">--</span>
          <span className="text-xs text-red-600 mt-1" role="alert">Failed to load</span>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-2 text-xs text-brand-accent underline hover:no-underline"
              aria-label={`Retry loading ${label}`}
            >
              Retry
            </button>
          )}
        </>
      ) : (
        <span className="text-3xl font-bold text-brand-text-primary">{value ?? 0}</span>
      )}
      <span className="text-sm text-brand-text-secondary mt-1">{label}</span>
      <span className="sr-only">{tooltip}</span>
    </div>
  );
}

export default function Dashboard() {
  // T011: Live KPI summary query (Feature 034) — independent of existing metrics query
  const {
    data: summary,
    isError: summaryError,
    refetch: retrySummary,
  } = useQuery<DashboardSummary>({
    queryKey: ['dashboardSummary'],
    queryFn: getDashboardSummary,
  });

  // T115: Single fetch on mount, no polling (server refreshes every 10 minutes)
  const { data: metrics, isLoading, isError, error, refetch } = useQuery<DashboardMetrics>({
    queryKey: ['dashboardMetrics'],
    queryFn: getDashboardMetrics,
  });

  // T117: Custom summary fetch for dashboard task widget counts
  const { data: taskSummary } = useQuery({
    queryKey: ['taskSummary'],
    queryFn: getTaskSummary,
  });


  // T116: Calculate "Last updated X minutes ago" from computedAt timestamp
  const lastUpdatedText = useMemo(() => {
    if (!metrics?.computedAt) return null;
    const now = new Date();
    const computedAt = new Date(metrics.computedAt);
    const minutesAgo = Math.floor((now.getTime() - computedAt.getTime()) / 60000);
    if (minutesAgo < 1) return 'Last updated just now';
    if (minutesAgo === 1) return 'Last updated 1 minute ago';
    return `Last updated ${minutesAgo} minutes ago`;
  }, [metrics?.computedAt]);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        {/* T117: Manual refresh button */}
        <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="px-4 py-2 bg-brand-accent text-brand-text-inverse rounded-full hover:bg-brand-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* T116: Display last updated timestamp */}
      {lastUpdatedText && (
        <p className="text-sm text-brand-text-secondary mb-4">{lastUpdatedText}</p>
      )}

      {/* T011: Six live KPI tiles (FR-009, FR-009a) */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        {KPI_TILES.map(tile => (
          <KpiTile
            key={tile.key}
            label={tile.label}
            tooltip={tile.tooltip}
            value={summary?.[tile.key]}
            isError={summaryError}
            onRetry={() => retrySummary()}
          />
        ))}
      </div>

      {/* NEW TASK SUMMARY WIDGET ROW */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <KpiTile
            label="New Tasks Today"
            tooltip="Tasks created during the current calendar day"
            value={taskSummary?.newTasksToday}
        />
        <KpiTile
            label="Open Tasks"
            tooltip="Active tasks awaiting completion"
            value={taskSummary?.openTasks}
        />
        <KpiTile
            label="Overdue Tasks"
            tooltip="Uncompleted tasks whose due dates have passed"
            value={taskSummary?.overdueTasks}
        />
      </div>

      {/* Error notification banner for Recent Activity section only */}
      {isError && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-4 mb-4">
          <p className="font-semibold">Error loading recent activity</p>
          <p className="text-sm">{(error as Error)?.message || 'Failed to fetch dashboard data'}</p>
        </div>
      )}

      {isLoading && <p className="text-brand-text-secondary">Loading recent activity…</p>}

      {/* FR-010: Recent activity section — always rendered independently of KPI query */}
      {metrics && (
        <>
          {/* Recent activity */}
          {metrics.recentActivity.length > 0 && (
            <div className="bg-white rounded shadow p-4">
              <h2 className="text-sm font-semibold mb-2 text-brand-text-secondary">Recent Activity</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-brand-text-secondary text-left">
                    <th className="py-1">Type</th>
                    <th className="py-1">ID</th>
                    <th className="py-1">Subject</th>
                    <th className="py-1">Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.recentActivity.map(activity => (
                    <tr key={`${activity.type}-${activity.id}`} className="border-t border-brand-border">
                      <td className="py-2">
                        <span className={`px-2 py-1 rounded text-xs ${activity.type === 'Ticket' ? 'bg-blue-100 text-blue-800' : 'bg-brand-accent/15 text-brand-accent'}`}>
                          {activity.type}
                        </span>
                      </td>
                      <td className="py-2 font-mono text-xs">{activity.internalId}</td>
                      <td className="py-2">{activity.subject}</td>
                      <td className="py-2 text-brand-text-secondary text-xs">
                        {new Date(activity.timestamp).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
